import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CurrencyDollarIcon, 
  CalculatorIcon, 
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  BanknotesIcon,
  ChartBarIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import payrollService from '../../services/payrollService';
import toast from 'react-hot-toast';

const PayrollManagement = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    month: '',
    year: new Date().getFullYear()
  });

  // Payroll processing states
  const [processingPayroll, setProcessingPayroll] = useState(false);
  const [processingMonth, setProcessingMonth] = useState('');
  const [processingYear, setProcessingYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadPayrollData();
  }, [filters]);

  const loadPayrollData = async () => {
    try {
      setLoading(true);
      const [payrollRes, salariesRes] = await Promise.all([
        payrollService.getPayrolls({ status: filters.status, payPeriod: filters.month ? `${filters.year}-${String(filters.month).padStart(2, '0')}` : undefined }),
        payrollService.getSalaries({ isActive: true })
      ]);
      const prs = payrollRes.data?.payrolls || payrollRes.data || payrollRes.payrolls || [];
      const sals = salariesRes.data?.salaries || salariesRes.data || salariesRes.salaries || [];
      setPayrolls(prs);
      setSalaries(sals.map(s => ({
        id: s.id,
        employeeName: `${s.Employee?.User?.firstName || ''} ${s.Employee?.User?.lastName || ''}`.trim(),
        baseSalary: s.basicSalary,
        allowances: s.allowances ? Object.values(s.allowances).reduce((sum, v) => sum + Number(v || 0), 0) : 0,
        totalCompensation: Number(s.basicSalary || 0) + (s.allowances ? Object.values(s.allowances).reduce((sum, v) => sum + Number(v || 0), 0) : 0)
      })));
    } catch (error) {
      console.error('Error loading payroll data:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayroll = async () => {
    if (!processingMonth || !processingYear) {
      toast.error('Please select month and year');
      return;
    }

    try {
      setProcessingPayroll(true);
      const monthIndex = new Date(`${processingMonth} 1, ${processingYear}`).getMonth() + 1;
      const payPeriod = `${processingYear}-${String(monthIndex).padStart(2, '0')}`;
      // Process payroll for all active salaries' employees
      const employeeIds = salaries.map(s => s.employeeId).filter(Boolean);
      await payrollService.processPayroll({ payPeriod, payDate: new Date().toISOString(), employeeIds });
      toast.success(`Payroll processed successfully for ${processingMonth} ${processingYear}`);
      setProcessingMonth('');
      setProcessingYear(new Date().getFullYear());
      loadPayrollData();
    } catch (error) {
      console.error('Error processing payroll:', error);
      toast.error('Failed to process payroll');
    } finally {
      setProcessingPayroll(false);
    }
  };

  const handleDeletePayroll = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payroll?')) return;
    
    try {
      toast.success('Payroll deleted successfully');
      loadPayrollData();
    } catch (error) {
      console.error('Error deleting payroll:', error);
      toast.error('Failed to delete payroll');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processed: 'bg-green-100 text-green-800',
      approved: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LYD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Payroll Management</h1>
              <p className="mt-2 text-gray-600">
                Manage employee salaries and payroll processing
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setProcessingMonth('January')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Process Payroll
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Payroll</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(5250)}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <UserIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Employees</p>
                <p className="text-2xl font-bold text-gray-900">2</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">1</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Processed</p>
                <p className="text-2xl font-bold text-gray-900">1</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Payroll List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Payrolls</h2>
          </div>
          
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading payroll data...</p>
            </div>
          ) : (
            <div className="p-6">
              {payrolls.length === 0 ? (
                <div className="text-center py-8">
                  <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No payrolls found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by processing your first payroll.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {payrolls.map((payroll) => (
                    <motion.div
                      key={payroll.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {payroll.employeeName || 'Employee'}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {formatDate(payroll.payrollDate)}
                            </p>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payroll.status)}`}>
                          {payroll.status}
                        </span>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Base Salary:</span>
                          <span className="text-sm font-medium">{formatCurrency(payroll.baseSalary || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Allowances:</span>
                          <span className="text-sm font-medium">{formatCurrency(payroll.allowances || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Deductions:</span>
                          <span className="text-sm font-medium text-red-600">-{formatCurrency(payroll.deductions || 0)}</span>
                        </div>
                        <div className="border-t pt-2">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium text-gray-900">Total:</span>
                            <span className="text-sm font-bold text-gray-900">{formatCurrency(payroll.totalAmount || 0)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          className="flex-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium focus:outline-none focus:underline"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleDeletePayroll(payroll.id)}
                          className="text-sm text-red-600 hover:text-red-800 font-medium focus:outline-none focus:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayrollManagement;